import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useUploadManagerUnified } from '@/components/documents-v2/hooks/useUploadManagerUnified';
import { DocumentSelectionModal } from '@/components/tasks/task-detail/DocumentSelectionModal';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Label } from '@/components/ui/label';
import { Z_INDEX_MODAL_NESTED } from '@/components/ui/zIndex';
import { useMatter } from '@/hooks/useMatter';
import {
  TreatmentRecordRequestSchema,
  TreatmentRecordRequestStatus,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

import {
  BaseRequestStep,
  BaseRequestStepProps,
  useUpdateRequestStatus,
} from './RequestStatusTrackerStep';

// Custom completion modal for Records Ingested step
const RecordIngestedModal = ({
  isOpen,
  onClose,
  nextStatus,
  request,
  providerId,
}: {
  isOpen: boolean;
  onClose: () => void;
  nextStatus: TreatmentRecordRequestStatus;
  request: TreatmentRecordRequestSchema;
  providerId: string;
}) => {
  const { matterId } = useMatter();
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedBlobIds, setSelectedBlobIds] = useState<string[]>([]);

  const { changeStatus, isLoading: isChangingStatus } = useUpdateRequestStatus({
    requestId: request.id,
    providerId,
  });

  const [ingestRecords, { isLoading: isSubmitting }] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsRecordsCreateMutation();

  const { handleUploadToCollection } = useUploadManagerUnified({
    matterId,
    refetch: () => {},
  });

  const handleDocumentSelection = async (blobIds: string[]) => {
    setSelectedBlobIds(blobIds);
    setShowDocumentModal(false);
  };

  const handleSubmitRecords = async () => {
    try {
      if (selectedBlobIds.length > 0) {
        // Submit records if documents are provided

        const promises = selectedBlobIds.map(async (blobId) => {
          return ingestRecords({
            matterId,
            providerId,
            requestId: request.id,
            createTreatmentRecordRequestRecordSchema: {
              blob_id: blobId,
            },
          });
        });

        await Promise.all(promises);

        toast.success('Records submitted successfully');
      }

      // Proceed to next status and pass blob IDs as attachments so the
      // backend can sync them to the associated task and complete it.
      await changeStatus(
        nextStatus,
        undefined,
        false,
        selectedBlobIds.length > 0 ? selectedBlobIds : undefined,
      );
      onClose();
    } catch {
      toast.error('Failed to submit records');
    }
  };

  const handleSkip = async () => {
    // Skip records upload and just change status
    await changeStatus(nextStatus, undefined);
    onClose();
  };

  const handleFirmAlreadyReceived = async () => {
    try {
      // Bypass upload — the firm already has the records physically.
      // Pass status change with a log note so the backend can complete
      // the task without requiring attachments.
      await changeStatus(
        nextStatus,
        { details: 'Records already received by firm' },
      );
      onClose();
    } catch {
      toast.error('Failed to update status');
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        size="lg"
        zIndex={Z_INDEX_MODAL_NESTED}
        nested
      >
        <DialogTitle>Submit Records</DialogTitle>
        <DialogDescription>
          Upload the medical records documents for this records request, or
          indicate the firm has already received them.
        </DialogDescription>
        <DialogBody>
          <div className="space-y-4">
            <div>
              <Label>Records Documents</Label>
              <div className="mt-2">
                {selectedBlobIds.length > 0 ? (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <span className="text-sm text-gray-700">
                      {selectedBlobIds.length} document
                      {selectedBlobIds.length !== 1 ? 's' : ''} selected
                    </span>
                    <Button
                      outline
                      onClick={() => setShowDocumentModal(true)}
                      disabled={isSubmitting || isChangingStatus}
                    >
                      Change
                    </Button>
                  </div>
                ) : (
                  <Button
                    outline
                    onClick={() => setShowDocumentModal(true)}
                    disabled={isSubmitting || isChangingStatus}
                    className="w-full"
                  >
                    Select or Upload Records
                  </Button>
                )}
              </div>
            </div>

            <DialogActions>
              <Button outline onClick={onClose} disabled={isSubmitting || isChangingStatus}>
                Cancel
              </Button>
              <Button
                color="zinc"
                onClick={handleFirmAlreadyReceived}
                disabled={isSubmitting || isChangingStatus}
              >
                Already Received by Firm
              </Button>
              <Button color="zinc" onClick={handleSkip} disabled={isSubmitting || isChangingStatus}>
                Skip
              </Button>
              <Button
                color="indigo-steel"
                onClick={handleSubmitRecords}
                disabled={isSubmitting || isChangingStatus || !selectedBlobIds.length}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Records & Complete'
                )}
              </Button>
            </DialogActions>
          </div>
        </DialogBody>
      </Dialog>

      {showDocumentModal && (
        <DocumentSelectionModal
          open={showDocumentModal}
          onClose={() => setShowDocumentModal(false)}
          matterId={matterId}
          onSelectDocuments={handleDocumentSelection}
          initialSelectedBlobIds={selectedBlobIds}
          onUpload={handleUploadToCollection}
        />
      )}
    </>
  );
};

export const RecordIngestedStep = ({
  state,
  title,
  nextStatus,
  provider,
  request,
  status,
  actionText,
}: BaseRequestStepProps) => {
  return (
    <BaseRequestStep
      state={state}
      title={title}
      nextStatus={nextStatus}
      provider={provider}
      request={request}
      status={status}
      actionText={actionText || 'Submit Records'}
      renderCompletionModal={(props) => (
        <RecordIngestedModal
          {...props}
          request={request}
          providerId={provider.id}
          nextStatus={nextStatus}
        />
      )}
    />
  );
};
